import {
  BooleanValueSchema,
  DateTimeValueSchemas,
  GeocoordinateSimpleValueSchema,
  IntegerValueSchema,
  ListValueSchema,
  NumberValueSchema,
  StringValueSchema,
} from "@validereinc/utilities";
import { z } from "zod";
import { AssetType } from "./AssetSchemas.ts";
import { FileUploadedSchema } from "./FileSchemas.ts";
import { IdentifierSchema } from "../util/index.ts";

export const ResourceLookup = {
  ...AssetType,
  FORM_SUBMISSION: "form_submission",
  FORM_SCHEMA: "form_schema",
} as const;
export type ResourceLookupType =
  (typeof ResourceLookup)[keyof typeof ResourceLookup];

export const AttributeDataType = {
  NUMBER: "number",
  STRING: "string",
  GEO_POINT: "geo_point",
  BOOLEAN: "boolean",
  INTEGER: "integer",
  PICK_LIST: "pick-list",
  MULTI_PICK_LIST: "multi-pick-list",
  NUMBER_ARRAY: "number-array",
  LOOKUP: "lookup",
  MULTI_LOOKUP: "multi-lookup",
  DATE: "date",
  DATE_TIME: "date-time",
  DATE_TIME_RANGE: "date-time-range",
  FILE: "file",
  MULTI_FILE: "multi-file",
} as const;
export type AttributeDataTypeType =
  (typeof AttributeDataType)[keyof typeof AttributeDataType];

// REVIEW: verify if this is correct as this list is not verifiable through Swagger
export const AttributeId = {
  emissions_recovered: "emissions_recovered",
  compressibility: "compressibility",
  device_type: "device_type",
  vessel_height: "vessel_height",
  vessel_inside_diameter: "vessel_inside_diameter",
  gas_composition: "gas_composition",
  recovery_fraction: "recovery_fraction",
  unlit_feed_gas: "unlit_feed_gas",
  pressure: "pressure",
  temperature: "temperature",
  fuel_hhv: "fuel_hhv",
  fuel_unit: "fuel_unit",
  n2o_mass_emission_factor: "n2o_mass_emission_factor",
  flare_combustion_efficiency: "flare_combustion_efficiency",
  device_bleed_type: "device_bleed_type",
  device_count: "device_count",
  pneumatic_device_efs: "pneumatic_device_efs",
} as const;
export type AttributeIdType = (typeof AttributeId)[keyof typeof AttributeId];

export const AttributeBaseSchema = z.object({
  id: z.string(),
  data_type: z.nativeEnum(AttributeDataType),
  name: z.string(),
  description: z.string(),
  is_required: z.boolean(),
  units: z.string().optional(), // REVIEW: "units" here but "unit" in custom attributes. talk to BE about this mismatch.
  substitutions: z.string().optional(),
});

export const AttributeStringSchema = z
  .object({
    data_type: z.literal(AttributeDataType.STRING),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeNumberSchema = z
  .object({
    data_type: z.literal(AttributeDataType.NUMBER),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeIntegerSchema = z
  .object({
    data_type: z.literal(AttributeDataType.INTEGER),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeNumberArraySchema = z
  .object({
    data_type: z.literal(AttributeDataType.NUMBER_ARRAY),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeBooleanSchema = z
  .object({
    data_type: z.literal(AttributeDataType.BOOLEAN),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeGeoPointSchema = z
  .object({
    data_type: z.literal(AttributeDataType.GEO_POINT),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeDateSchema = z
  .object({
    data_type: z.literal(AttributeDataType.DATE),
    date_resolution: z
      .enum(["year", "year_month", "year_month_day"])
      .default("year_month_day"),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeDateTimeSchema = z
  .object({
    data_type: z.literal(AttributeDataType.DATE_TIME),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeDateTimeRangeSchema = z
  .object({
    data_type: z.literal(AttributeDataType.DATE_TIME_RANGE),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeFileSchema = z
  .object({
    data_type: z.literal(AttributeDataType.FILE),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributePicklistSchema = z
  .object({
    data_type: z.literal(AttributeDataType.PICK_LIST),
    pick_list_values: z.array(z.string()).optional(),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeMultiPicklistSchema = z
  .object({
    data_type: z.literal(AttributeDataType.MULTI_PICK_LIST),
    pick_list_values: z.array(z.string()).optional(),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeLookupSchema = z
  .object({
    data_type: z.literal(AttributeDataType.LOOKUP),
    lookup_entity_type: z.nativeEnum(ResourceLookup).optional(),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeMultiLookupSchema = z
  .object({
    data_type: z.literal(AttributeDataType.MULTI_LOOKUP),
    lookup_entity_type: z.nativeEnum(ResourceLookup).optional(),
  })
  .merge(AttributeBaseSchema.omit({ data_type: true }));

export const AttributeStrictSchema = z.discriminatedUnion("data_type", [
  AttributeStringSchema,
  AttributeNumberSchema,
  AttributeIntegerSchema,
  AttributeNumberArraySchema,
  AttributeBooleanSchema,
  AttributeGeoPointSchema,
  AttributeDateSchema,
  AttributeDateTimeSchema,
  AttributeDateTimeRangeSchema,
  AttributeFileSchema,
  AttributePicklistSchema,
  AttributeMultiPicklistSchema,
  AttributeLookupSchema,
  AttributeMultiLookupSchema,
]);

// IMPROVE: rely on discriminated union on "data_type" above when Zod supports
// nested discriminated unions better
export const AttributeSchema = AttributeBaseSchema.merge(
  z.object({
    pick_list_values: AttributePicklistSchema.shape.pick_list_values,
    minimum: AttributeNumberSchema.shape.minimum,
    maximum: AttributeNumberSchema.shape.maximum,
    lookup_entity_type: AttributeLookupSchema.shape.lookup_entity_type,
    date_resolution: AttributeDateSchema.shape.date_resolution.optional(),
  })
);
export type AttributeType = z.infer<typeof AttributeSchema>;

export const LookupValueSchema = IdentifierSchema;
export const LookupWithDetailsValueSchema = z
  .object({
    entity_type: z.nativeEnum(ResourceLookup),
    name: z.string(),
    value: LookupValueSchema,
  })
  .describe(
    "The lookup value for specific endpoints of the Node API, like Form Submission Answers"
  );
export type LookupWithDetailsValueType = z.infer<
  typeof LookupWithDetailsValueSchema
>;
export const LookupWithSingleAttributeRefValueSchema = z
  .object({
    id: LookupValueSchema,
    value: z
      .any()
      .describe("The value of the specified attribute on the looked up entity"),
    entity_type: z.nativeEnum(ResourceLookup),
  })
  .describe(
    "The lookup value for specific endpoints of the Node API, like Reports"
  );
export type LookupWithSingleAttributeRefValueType = z.infer<
  typeof LookupWithSingleAttributeRefValueSchema
>;

export const LookupAnswerValueSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    entity_type: z.nativeEnum(ResourceLookup),
  })
  .describe(
    "The lookup value for specific endpoints of the Node API, like Custom Attributes"
  );
export type LookupAnswerValueType = z.infer<typeof LookupAnswerValueSchema>;

export const AttributeValueSchema = z.union([
  StringValueSchema,
  NumberValueSchema,
  IntegerValueSchema,
  BooleanValueSchema,
  ListValueSchema,
  DateTimeValueSchemas.dateTime,
  DateTimeValueSchemas.dateTimeStrict,
  DateTimeValueSchemas.dateTimeRangeSimple,
  LookupValueSchema,
  GeocoordinateSimpleValueSchema,
  FileUploadedSchema,
  z.array(FileUploadedSchema),
]);
export type AttributeValueType = z.infer<typeof AttributeValueSchema>;

export const AttributeSchemaHelpers = {
  getDataTypeForDisplay: (value: AttributeType["data_type"]) => {
    switch (value) {
      case AttributeDataType.STRING:
        return "Plain Text";
      case AttributeDataType.BOOLEAN:
        return "Yes / No or True / False";
      case AttributeDataType.DATE:
        return "Date";
      case AttributeDataType.DATE_TIME:
        return "Date and Time";
      case AttributeDataType.DATE_TIME_RANGE:
        return "Date and Time Range";
      case AttributeDataType.FILE:
        return "File";
      case AttributeDataType.MULTI_FILE:
        return "Multi File";
      case AttributeDataType.NUMBER:
        return "Number (can have decimals)";
      case AttributeDataType.NUMBER_ARRAY:
        return "Number Array";
      case AttributeDataType.INTEGER:
        return "Integer";
      case AttributeDataType.LOOKUP:
        return "Lookup";
      case AttributeDataType.MULTI_LOOKUP:
        return "Multi Lookup";
      case AttributeDataType.GEO_POINT:
        return "Geo Coordinates";
      case AttributeDataType.PICK_LIST:
        return "Single Pick List";
      case AttributeDataType.MULTI_PICK_LIST:
        return "Multi Pick List";
    }
  },
};
